import React, { useState } from "react";
import { Box, TextField, Button, Typography } from "@mui/material";

import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { useAuth } from "../../context/AuthContext";
import Logo from "../../assets/logoazul.png";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

/**
 * REGEX
 */
const nameRegex = /^[A-ZÁÉÍÓÚÂÊÎÔÛÃÕÇ][a-záéíóúâêîôûãõç]+$/;
const phoneRegex = /^\d{10,11}$/;
const cpfRegex = /^\d{11}$/;
const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;

const BLACKLIST_DOMAINS = new Set([
  "yopmail.com",
  "yopmail.fr",
  "yopmail.net",
  "cool.fr.nf",
  "jetable.fr.nf",
  "nospam.ze.tc",
  "nomail.xl.cx",
  "mega.zik.dj",
  "speed.1s.fr",
  "courriel.fr.nf",
  "moncourrier.fr.nf",
  "mailinator.com",
  "mailinator.net",
  "mailinator.org",
  "mailinator2.com",
  "mailinator.gq",
  "mailinator.ga",
  "mailinator.ml",
  "mailinator.tk",
  "guerrillamail.com",
  "guerrillamail.net",
  "guerrillamail.org",
  "guerrillamail.info",
  "guerrillamailblock.com",
  "sharklasers.com",
  "tempmail.com",
  "temp-mail.org",
  "temp-mail.io",
  "temp-mail.ru",
  "tempmail.net",
  "tempmailo.com",
  "tempemail.net",
  "10minutemail.com",
  "10minutemail.net",
  "10minutemail.org",
  "getnada.com",
  "nada.email",
  "throwawaymail.com",
  "fakeinbox.com",
  "fakemail.net",
  "trashmail.com",
  "trashmail.net",
  "trashmail.org",
  "trash-mail.com",
  "maildrop.cc",
  "mailnesia.com",
  "inboxbear.com",
  "mintemail.com",
  "mailnull.com",
  "dispostable.com",
  "spamgourmet.com",
  "spambox.us",
  "spamfree24.org",
  "spamex.com",
  "emailondeck.com",
  "mytemp.email",
  "temporaryemail.net",
  "tempr.email",
  "mail-temporaire.fr",
  "mail-temporaire.com",
  "tempinbox.com"
]);

export default function RegisterForm({ onSwitch }) {
  const { register } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birth, setBirth] = useState(null);
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  function showError(message) {
    setDialogMessage(message);
    setDialogOpen(true);
  }

  function isAdult(date) {
    return dayjs().diff(dayjs(date), "year") >= 18;
  }

  function formatCPF(value) {
    const digits = value.replace(/\D/g, "").slice(0, 11);

    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }

  function isBlacklistedEmail(email) {
    if (!email.includes("@")) return true;

    const domain = email.split("@")[1].toLowerCase().trim();
    return BLACKLIST_DOMAINS.has(domain);
  }

  async function handleSubmit() {
    if (
      !firstName ||
      !lastName ||
      !birth ||
      !email ||
      !password ||
      !confirm ||
      !phone ||
      !cpf
    ) {
      return showError("Preencha todos os campos.");
    }

    if (isBlacklistedEmail(email)) {
      return showError(
        "Este domínio de email não é permitido. Utilize um email válido e permanente."
      );
    }

    if (!nameRegex.test(firstName)) {
      return showError("Nome inválido. Use apenas letras e primeira letra maiúscula.");
    }

    if (!nameRegex.test(lastName)) {
      return showError("Sobrenome inválido. Use apenas letras e primeira letra maiúscula.");
    }

    if (!phoneRegex.test(phone)) {
      return showError("Telefone inválido. Use apenas DDD + número (ex: 31983711234).");
    }

    if (!cpfRegex.test(cpf.replace(/\D/g, ""))) {
      return showError("CPF inválido.");
    }

    if (!isAdult(birth)) {
      return showError("Você precisa ser maior de 18 anos.");
    }

    if (!passwordRegex.test(password)) {
      return showError("A senha deve conter no mínimo 6 caracteres, com letras e números.");
    }

    if (password !== confirm) {
      return showError("As senhas não coincidem.");
    }

    if (!birth || !dayjs(birth).isValid()) {
      return showError("Data de nascimento inválida.");
    }

    setLoading(true);

    const res = await register({
      firstName,
      lastName,
      email,
      phone,
      cpf: cpf.replace(/\D/g, ""),
      password,
      birthDate: birth.valueOf(),
    });

    if (!res.ok) {
      setLoading(false);
      return showError("Erro ao criar conta. Email já pode estar em uso.");
    }

    window.location.href = "/";
  }

  return (
    <Box>
      <img src={Logo} alt="logo" style={{ width: 150, marginBottom: 20 }} />

      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Crie sua conta
      </Typography>

      <Typography sx={{ mt: 1, mb: 3, opacity: 0.6 }}>
        Cadastre-se no Emma
      </Typography>

      <TextField
        fullWidth
        label="Nome"
        margin="normal"
        inputProps={{ maxLength: 40 }}
        value={firstName}
        onChange={(e) =>
          setFirstName(
            e.target.value
              .replace(/[^a-zA-ZÀ-ÿ]/g, "")
              .replace(/^./, (c) => c.toUpperCase())
          )
        }
        sx={{
          "& .MuiInputLabel-root": {
            color: "#9e9e9e",
          },
        }}
      />

      <TextField
        fullWidth
        label="Sobrenome"
        margin="normal"
        inputProps={{ maxLength: 40 }}
        value={lastName}
        onChange={(e) =>
          setLastName(
            e.target.value
              .replace(/[^a-zA-ZÀ-ÿ]/g, "")
              .replace(/^./, (c) => c.toUpperCase())
          )
        }
        sx={{
          "& .MuiInputLabel-root": {
            color: "#9e9e9e",
          },
        }}
      />

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <DatePicker
          label="Data de nascimento"
          format="DD/MM/YYYY"
          value={birth}
          onChange={setBirth}
          maxDate={dayjs().subtract(18, "year")}
          slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
          sx={{
            "& .MuiInputLabel-root": {
              color: "#9e9e9e",
            },
          }}
        />
      </LocalizationProvider>

      <TextField
        fullWidth
        label="Telefone"
        placeholder="(11) 99156-7807"
        margin="normal"
        value={phone}
        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
        sx={{
          "& .MuiInputLabel-root": {
            color: "#9e9e9e",
          },
        }}
      />

      <TextField
        fullWidth
        label="CPF"
        margin="normal"
        value={cpf}
        onChange={(e) => setCpf(formatCPF(e.target.value))}
        sx={{
          "& .MuiInputLabel-root": {
            color: "#9e9e9e",
          },
        }}
      />

      <TextField
        fullWidth
        label="Email"
        margin="normal"
        inputProps={{ maxLength: 120 }}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{
          "& .MuiInputLabel-root": {
            color: "#9e9e9e",
          },
        }}
      />

      <TextField
        fullWidth
        label="Senha"
        type="password"
        margin="normal"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        sx={{
          "& .MuiInputLabel-root": {
            color: "#9e9e9e",
          },
        }}
      />

      <TextField
        fullWidth
        label="Confirmar senha"
        type="password"
        margin="normal"
        value={confirm}
        helperText="Mínimo 6 caracteres, contendo letras e números"
        onChange={(e) => setConfirm(e.target.value)}
        sx={{
          "& .MuiInputLabel-root": {
            color: "#9e9e9e",
          },
          "& .MuiFormHelperText-root": {
            color: "#9e9e9e",
          },
        }}
      />

      <Button
        fullWidth
        variant="contained"
        disabled={loading}
        onClick={handleSubmit}
        sx={{
          mt: 3,
          py: 1.4,
          borderRadius: "12px",
          background: "var(--azul)",
          ":hover": { background: "var(--azul)" },
        }}
      >
        {loading ? "Criando..." : "Criar conta"}
      </Button>

      <Typography
        sx={{ mt: 3, textAlign: "center", fontSize: 14, cursor: "pointer", textDecoration: "underline" }}
        onClick={onSwitch}
      >
        Já tem uma conta? Entrar
      </Typography>


      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Atenção
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {dialogMessage}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="contained"
            sx={{
              background: "var(--azul)",
              ":hover": { background: "var(--azul)" },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
